import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "row no-wrap" }
const _hoisted_2 = { class: "flex flex-center fit" }
const _hoisted_3 = { class: "q-px-md items-center line-clamp ellipsis" }

import { AxiosError, isAxiosError } from "axios"
import { Log } from "src/util/classes/log"
import { IMG_PLACEHOLDER_SM } from "src/util/constants/files"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import {
  getAttachmentContent,
  getAttachmentContextFromEntityPage,
  getAttachmentData,
  getFirstImageAttachment,
} from "src/util/functions/attachments"
import {
  fetchAttachmentsListForEntity,
  getAttachmentForEntity,
} from "src/util/functions/entityData"
import { RowData, TableImage } from "src/util/types/components/sfxTable"
import { Attachment } from "src/util/types/dataModels/attachment"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"
import { computed, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import SfxTooltip from "../tooltip/SfxTooltip.vue"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxTableTdImage',
  props: {
  uniqueColumn: {
    type: [String, Function],
    required: true,
  },

  text: [String, Number],

  image: Object as PropType<TableImage>,

  hasIdentifier: Boolean,

  imagePlaceholder: String,

  entity: Object as PropType<RowData>,

  entityPage: String as PropType<EntityPages>,
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()

const textClass = computed(() => (props.text ? "text-h6" : "text-no-value"))

const cellImage = ref<string | undefined>()

const placeholder = computed(() => props.imagePlaceholder ?? IMG_PLACEHOLDER_SM)

//endregion

//region Functions

async function getAttachmentString(
  attachmentUUID: string,
  entityPage: EntityPages,
  entity: RowData
) {
  try {
    const attachmentContext = getAttachmentContextFromEntityPage(entityPage, entity)

    if (!attachmentContext) return

    const entityId: string =
      typeof props.uniqueColumn === "function"
        ? props.uniqueColumn(entity)
        : entity[props.uniqueColumn as "string"]

    let attachment: Attachment | null | undefined = await getAttachmentData(attachmentUUID)

    if (!attachment) {
      attachment = await getAttachmentForEntity(attachmentContext, entityId, attachmentUUID)
    }

    if (attachment) {
      return getAttachmentContent(attachment, attachmentContext, entityId)
    }
  } catch (error) {
    if (isAxiosError(error) && (error as AxiosError).response?.status === 404) return
    Log.error({ message: "Failed to fetch attachment in table", error })
  }
}

async function getAttachmentStringFromList(
  attachmentList: string[],
  entityPage: EntityPages,
  entity: RowData
) {
  try {
    const attachmentContext = getAttachmentContextFromEntityPage(entityPage, entity)

    if (!attachmentContext) return

    const entityId: string =
      typeof props.uniqueColumn === "function"
        ? props.uniqueColumn(entity)
        : entity[props.uniqueColumn as "string"]

    await fetchAttachmentsListForEntity(attachmentContext, entityId)

    const attachment = await getFirstImageAttachment(attachmentList)

    if (attachment) {
      return getAttachmentContent(attachment, attachmentContext, entityId)
    }
  } catch (error) {
    if (isAxiosError(error) && (error as AxiosError).response?.status === 404) return
    Log.error({ message: "Failed to fetch attachment in table", error })
  }
}

//endregion

//region Setup Code

watch(
  () => props.image,
  async (imageSource) => {
    if (imageSource) {
      let imageContent

      if ("url" in imageSource) {
        imageContent = imageSource.url
      } else if (props.entityPage && props.entity && imageSource.uuid) {
        if (Array.isArray(imageSource.uuid)) {
          imageContent = await getAttachmentStringFromList(
            imageSource.uuid,
            props.entityPage,
            props.entity
          )
        } else {
          imageContent = await getAttachmentString(imageSource.uuid, props.entityPage, props.entity)
        }
      }

      cellImage.value = imageContent
    }
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_img = _resolveComponent("q-img")!
  const _component_q_td = _resolveComponent("q-td")!

  return (_openBlock(), _createBlock(_component_q_td, {
    class: _normalizeClass(["sfx-table__td sfx-table__td-image text-center", textClass.value])
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_q_img, {
          src: cellImage.value,
          "placeholder-src": placeholder.value,
          fit: "cover",
          height: "100%"
        }, {
          error: _withCtx(() => [
            _createElementVNode("span", _hoisted_2, [
              _createVNode(_component_q_icon, {
                name: _unref(Icons).IMAGE_ERROR,
                class: "text-quote-text",
                size: "35px"
              }, null, 8, ["name"])
            ])
          ]),
          _: 1
        }, 8, ["src", "placeholder-src"]),
        _createElementVNode("div", _hoisted_3, _toDisplayString(props.text || _ctx.$t("sfxTable.empty_no_value")), 1)
      ]),
      (props.hasIdentifier)
        ? (_openBlock(), _createBlock(_component_q_icon, {
            key: 0,
            class: "identify-icon",
            name: _unref(Icons).IDENTIFY,
            color: "inactive",
            size: "16px"
          }, {
            default: _withCtx(() => [
              _createVNode(SfxTooltip, {
                data: { text: _unref(t)('sfxTooltip.has_identifier'), isHtml: true }
              }, null, 8, ["data"])
            ]),
            _: 1
          }, 8, ["name"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["class"]))
}
}

})